import * as _ from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiModel } from 'models';
import {
    HistoricalOrder,
    Order,
    Product,
    Prepack,
    HistoricalOrderArticle,
    HistoricalOrderOverview
} from './searcher.types';
import { deburrAndLower, getFormattedColor, getArticlePrice, getArticleMainImageUrl, AvailableLanguage } from 'helpers';

import { ReorderArticleSize } from 'app/b2b-order/types/reorder';
import { adaptReorderArticleToOrderMatrix } from './adapter-generator';
import { SearchParameters } from 'services';
import { stringify } from 'query-string';
import { OrderHistoryFacets } from 'services/product-search/facets-ui-mapper';
import { sortSizes } from 'app/b2b-order/helpers/size';
import { getFormattedPrice } from 'app/b2b-order/helpers/price';

export class Searcher {
    constructor(private apiModel: ApiModel) {}

    public getOrders({ query, filters, nextId }: SearchParameters): Observable<{
        orders: HistoricalOrderOverview[];
        nextId: string;
        facets: OrderHistoryFacets;
    }> {
        const queryParams = {
            ...filters,
            duration: filters.orderDates,
            ...(query ? { search: query } : {}),
            limit: 10,
            afterId: undefined
        };
        if (nextId) {
            queryParams.afterId = nextId;
        }
        const urlGetAll = `${this.apiModel.getOrderEndpoint()}?${stringify(queryParams, {
            arrayFormat: 'bracket',
            sort: false
        })}`;

        return combineLatest([this.apiModel.makeHttpObservableRequest({ method: 'GET', url: urlGetAll })]).pipe(
            map(
                ([{ nextId, overviewList: orders, facets }]: [
                    { nextId: string; overviewList: HistoricalOrder[]; facets: OrderHistoryFacets }
                ]) => {
                    const historicalOrders = _.map(orders, (order) => ({
                        ...order
                    }));
                    return { orders: historicalOrders, nextId, facets };
                }
            )
        );
    }

    public getOrder(orderId: string, type?: string): Observable<HistoricalOrder> {
        const preorderOrderTypes = ['fc-platform-preorder', 'showroom'];
        const url = preorderOrderTypes.includes(type)
            ? `${this.apiModel.getOrderHistoryEndpoint()}/preorder/${orderId}`
            : `${this.apiModel.getOrderEndpoint()}/${orderId}`;
        return this.apiModel.makeHttpObservableRequest({ method: 'GET', url }).pipe(
            map((order: HistoricalOrder) => {
                const orderedItemsByBrand = this.groupOrderItemsByBrand(order);
                const retailerContactData = order.retailerContactData;
                const shippingAddressName =
                    order.shippingAddress.name !== order.retailerOrganizationName
                        ? `${order.shippingAddress.name}, `
                        : '';
                return {
                    ...order,
                    ...(retailerContactData ? { retailerContactData } : {}),
                    storeName: `${shippingAddressName} ${order.shippingAddress.address1}, ${order.shippingAddress.zip} ${order.shippingAddress.city}`,
                    total: {
                        items: _.sumBy(orderedItemsByBrand, ({ articles }) =>
                            _.sumBy(articles, ({ total }) => total.prepacks + total.sizes)
                        ),
                        articles: this.getTotalArticles(orderedItemsByBrand),
                        price: order.products.reduce((acc, { price }) => Number(price?.value) + acc, 0) || '--'
                    },
                    hasPrepacks: !_.isEmpty(order.groupedPrepacks),
                    orderedItemsByBrand: _.sortBy(orderedItemsByBrand, ({ brandName }) => deburrAndLower(brandName))
                };
            })
        );
    }

    private getTotalArticles(
        orderedItemsByBrand: {
            brandId: string;
            brandName: string;
            articles: HistoricalOrderArticle[];
        }[]
    ): number {
        const uniqueArticles = new Set([]);
        for (const orderedItem of orderedItemsByBrand) {
            orderedItem.articles.forEach((article: HistoricalOrderArticle) => {
                const uniqueKey = `${article.color.colorGroupId}-${article.styleGroupId}`;
                uniqueArticles.add(uniqueKey);
            });
        }
        return uniqueArticles.size;
    }

    private groupOrderItemsByBrand(order: Order): {
        brandId: string;
        brandName: string;
        articles: HistoricalOrderArticle[];
    }[] {
        const productsGroupedByBrand = _.groupBy(order.groupedProducts, 'brandId');
        const prepacksGroupedByBrand = _.groupBy(order.groupedPrepacks, 'brandId');
        const brandIds = _.union(_.keys(productsGroupedByBrand), _.keys(prepacksGroupedByBrand));

        return _.map(brandIds, (brandId) => {
            const products = _.map(
                _.get(productsGroupedByBrand, `[${brandId}][0].products`) as unknown as Product[],
                (product) => ({
                    ...product,
                    orderedAmount: _.get(
                        _.find(order.products, ({ gtin }) => gtin === product.gtin),
                        'quantity',
                        0
                    )
                })
            );
            const prepacks = _.get(prepacksGroupedByBrand, `[${brandId}][0].prepacks`) as unknown as Prepack[];
            const brandName = (_.get(productsGroupedByBrand, `[${brandId}][0].brandName`) ||
                _.get(prepacksGroupedByBrand, `[${brandId}][0].brandName`)) as unknown as string;
            return {
                brandId,
                brandName,
                articles: this.mapProductsToArticle({ products, prepacks, brandId, brandName })
            };
        });
    }

    private mapProductsToArticle({
        products,
        prepacks,
        brandId,
        brandName
    }: {
        products: (Product & { orderedAmount: number })[];
        prepacks: Prepack[];
        brandId: string;
        brandName: string;
    }): HistoricalOrderArticle[] {
        const productGroupedByColor = _.groupBy(
            products,
            ({ colorGroupId, styleGroupId }) => `${styleGroupId}_${colorGroupId}`
        );
        const prepackGroupedByColor = _.groupBy(
            prepacks,
            ({ colorGroupId, styleGroupId }) => `${styleGroupId}_${colorGroupId}`
        );
        const colorGroupIds = _.union(_.keys(prepackGroupedByColor), _.keys(productGroupedByColor));

        const articles = _.map(colorGroupIds, (colorGroupId) => {
            const colorProducts = productGroupedByColor[colorGroupId];
            const colorPrepacks = prepackGroupedByColor[colorGroupId];
            const firstOrderedItem = (colorProducts || colorPrepacks)[0];

            const sizes = _.map(colorProducts, (product) =>
                this.mapProductToReorderArticleSize({ product, brandName })
            );
            const articlePrice = getArticlePrice(colorProducts);
            const color = {
                code: _.get(firstOrderedItem, 'manufacturerAttributes.colorCode'),
                name: _.get(firstOrderedItem, 'manufacturerAttributes.colorName.de'),
                colorGroupId: firstOrderedItem.colorGroupId
            };

            const article = {
                brandId,
                brandName,
                styleGroupId: _.get(firstOrderedItem, 'styleGroupId'),
                articleNumber: firstOrderedItem.articleNumber,
                name: _.get(firstOrderedItem, 'manufacturerAttributes.name.de'),
                images: [],
                imageUrl: getArticleMainImageUrl(colorProducts),
                isPreliminary: false,
                showsDefaultPricelist: _.get(firstOrderedItem, 'showsDefaultPricelist', false),
                minimumOrderRestriction: _.get(firstOrderedItem, 'minimumOrderRestriction')
                    ? { ..._.get(firstOrderedItem, 'minimumOrderRestriction'), value: '' }
                    : undefined,
                stock: { available: 0, validSelectedAmount: true },
                hasDiscountedPurchasePrice: !!articlePrice.discountedPurchasePrice,
                manufacturerAttributes: {},
                fcAttributes: {},
                color,
                formattedColor: getFormattedColor(color),
                sizes: sortSizes(sizes) as ReorderArticleSize[],
                price: articlePrice,
                formattedPrice: getFormattedPrice(articlePrice, AvailableLanguage.En),
                prepacks: _.map(colorPrepacks, (prepack) => ({
                    ...prepack,
                    _id: prepack.prepackId,
                    brand: brandId,
                    products: [],
                    stock: {
                        available: prepack.quantity,
                        total: prepack.quantity,
                        selectedAmount: prepack.quantity
                    }
                })),
                total: {
                    prepacks: _.sumBy(colorPrepacks, 'quantity'),
                    sizes: _.sumBy(sizes, 'stock.selectedAmount')
                }
            };

            return {
                ...article,
                forOrderMatrix: adaptReorderArticleToOrderMatrix(article)
            };
        });

        return articles;
    }

    private mapProductToReorderArticleSize({
        product,
        brandName
    }: {
        product: Product & { orderedAmount: number };
        brandName: string;
    }): ReorderArticleSize {
        return {
            gtin: product.gtin,
            name: _.get(product, 'manufacturerAttributes.name.de'),
            size: _.toUpper(_.get(product, 'manufacturerAttributes.size.de')),
            brandName,
            articleNumber: product.articleNumber,
            showsDefaultPricelist: product.showsDefaultPricelist,
            images: product.media.images,
            stock: {
                available: product.stock.available || 0,
                total: product.stock.available + _.get(product.stock, 'backorder.available', 0),
                backorder: product.stock.backorder,
                selectedAmount: product.orderedAmount || 0
            },
            manufacturerAttributes: product.manufacturerAttributes,
            fcAttributes: product.fcAttributes,
            price: product.price,
            formattedPrice: getFormattedPrice(product.price, AvailableLanguage.En)
        };
    }
}
